import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import * as React from 'react';

import { SpinningIcon } from '@/components/animation/SpinningIcon/SpinningIcon';
import { RefreshCw05Icon } from '@/components/icons/RefreshCw05Icon';
import { Card } from '@/components/layout/Card/Card';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useIsOverflowing } from '@/hooks/useIsOverflowing';
import { COLORS } from '@/styles/tokens/colors';

import { AlertTriangleIcon } from '../icons/AlertTriangleIcon';

export type DocumentCardStatus = 'default' | 'progress' | 'complete' | 'error';

export interface DocumentCardProps {
  /**
   * Document filename to display
   */
  filename: string;
  /**
   * Current status of the document
   */
  status: DocumentCardStatus;
  /**
   * Message to display below the filename. Mostly used for error messages.
   */
  message?: string;
  /**
   * Custom controls to render in the card
   */
  controls?: React.ReactNode;
  /**
   * Optional styles to apply to the card
   */
  sx?: SxProps<Theme>;
}

export function DocumentCard({
  filename,
  status,
  message,
  controls,
  sx,
}: DocumentCardProps) {
  const isError = status === 'error';
  const [filenameRef, isFilenameOverflowing] =
    useIsOverflowing<HTMLSpanElement>([filename]);

  // Render the appropriate icon based on status
  const renderIcon = () => {
    switch (status) {
      case 'progress':
        return (
          <SpinningIcon
            Icon={RefreshCw05Icon}
            size={16}
            color={COLORS.TEXT.SECONDARY}
          />
        );
      case 'error':
        return <AlertTriangleIcon size={16} color={COLORS.ORANGE[500]} />;
      default:
        return null;
    }
  };
  return (
    <Card
      variant={isError ? 'error' : 'outlined'}
      sx={{
        py: 2,
        px: 2.5,
        width: '100%',
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        {status !== 'default' && status !== 'complete' && (
          <Box display="flex" alignItems="center" justifyContent="center">
            {renderIcon()}
          </Box>
        )}
        <Stack flexGrow={1} spacing={0.5} sx={{ minWidth: 0 }}>
          {!isError && <Typography variant="body-xs">Document name</Typography>}
          <Tooltip title={filename} show={isFilenameOverflowing}>
            <Typography
              ref={filenameRef}
              variant="title-s"
              component="span"
              color={isError ? COLORS.ORANGE[900] : undefined}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                // display:block needed so the span respects width for overflow detection
                display: 'block',
              }}
            >
              {filename}
            </Typography>
          </Tooltip>
          {message && (
            <Typography
              variant="body-xs"
              color={isError ? COLORS.ORANGE[900] : undefined}
            >
              {message}
            </Typography>
          )}
        </Stack>
        {controls && (
          <Box ml="auto" sx={{ flexShrink: 0 }}>
            {controls}
          </Box>
        )}
      </Stack>
    </Card>
  );
}
